import React, { useEffect, useMemo, useState } from "react";
import {
    CardBody,
    CardHeader,
    Spinner,
    Alert,
    Button,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Input,
    Label,
    FormGroup,
    Row,
    Col,
} from "reactstrap";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { API } from "../../api/urls";
import { TURKISH_BANKS, CURRENCIES } from "../../constants/paymentConstants";

import useIdempotencyKey from "../../hooks/useIdempotencyKey";
import { describeAxiosError } from "../../utils/apiErrors";
import DateInput from "../../components/Common/DateInput";

const NAVY   = "#221C5D";
const ORANGE = "#E56A22";

const TableCard = ({ className = "", children, ...rest }) => (
    <div
        className={`card rounded-4 border-0 shadow-lg ${className}`}
        style={{ boxShadow: "0 .5rem 1.25rem rgba(10,49,110,.08)" }}
        {...rest}
    >
        {children}
    </div>
);

const tableStyles = `
.vade-table .table thead tr { background-color: #f9f9f9; }
.vade-table .table th { border: 0; color: #6c757d; font-weight: 600; font-size: .8rem; text-transform: uppercase; letter-spacing: .04em; }
.vade-table .table td { vertical-align: middle; font-size: .875rem; }
.vade-table .table-hover tbody tr:hover { background: #fcfcfd; }
.vade-table .header-actions { gap: .5rem; }
.vade-table .btn-rounded { border-radius: 999px; }
.vade-table .btn-navy  { background: ${NAVY};   border-color: ${NAVY};   color: #fff; }
.vade-table .btn-navy:hover  { filter: brightness(1.03); color: #fff; }
.vade-table .btn-orange { background: ${ORANGE}; border-color: ${ORANGE}; color: #fff; }
.vade-table .btn-orange:hover { filter: brightness(1.03); color: #fff; }
.vade-table .filter-pill { border-radius: 999px; padding: .3rem .85rem; font-size: .8rem; border: 1.5px solid #dee2e6; background: #fff; color: #6c757d; transition: all .15s; white-space: nowrap; }
.vade-table .filter-pill.active { background: ${NAVY}; border-color: ${NAVY}; color: #fff; }
.vade-table .filter-pill:hover:not(.active):not(:disabled) { border-color: ${NAVY}; color: ${NAVY}; }
.vade-table .filter-pill:disabled { opacity: .5; cursor: not-allowed; }
.vade-table .doc-code { font-family: monospace; background: #f1f3f5; border-radius: 6px; padding: 2px 8px; font-size: .8rem; color: #343a40; }
.vade-table .pill-input, .vade-table .pill-select { border-radius: 999px !important; padding: .45rem .9rem; }
.vade-table .corporate-pagination .page-link { color: ${NAVY}; border-color: ${ORANGE}; }
.vade-table .corporate-pagination .page-item.active .page-link { background-color: ${NAVY}; border-color: ${NAVY}; color: #fff; }
.vade-table .corporate-pagination .page-link:hover { border-color: ${NAVY}; color: ${NAVY}; }
@media (max-width: 576px) { .vade-table .table { font-size: 12px; } }
`;

const remainingOf = (item) => item.remainingAmount ?? item.amount;

const StatusPill = ({ item }) => {
    if (item.invoiceStatus === "CLOSED")
        return <span className="badge rounded-pill px-3 py-2" style={{ backgroundColor: "#198754", color: "#fff", fontSize: ".75rem" }}>Ödendi</span>;
    // Kısmen ödenmiş borç açık sayılır; vadesi geçmişse Vade sütunu bunu zaten kırmızı gösteriyor.
    if (item.invoiceStatus === "PARTIALLY_PAID")
        return <span className="badge rounded-pill px-3 py-2" style={{ backgroundColor: "#0dcaf0", color: "#fff", fontSize: ".75rem" }}>Kısmen Ödendi</span>;
    if (item.overdue)
        return <span className="badge rounded-pill px-3 py-2" style={{ backgroundColor: "#dc3545", color: "#fff", fontSize: ".75rem" }}>Vadesi Geçmiş</span>;
    return <span className="badge rounded-pill px-3 py-2" style={{ backgroundColor: "#fd7e14", color: "#fff", fontSize: ".75rem" }}>Bekleyen</span>;
};

// Mahsuplaşma gönderimi cari bazlıdır; aynı carinin tüm satırları aynı değeri taşır.
const ReconciliationSentPill = ({ sentAt, sentTo }) => {
    if (!sentAt)
        return <span className="badge rounded-pill px-3 py-2" style={{ backgroundColor: "#e9ecef", color: "#6c757d", fontSize: ".75rem" }}>Gönderilmedi</span>;
    return (
        <span
            className="badge rounded-pill px-3 py-2"
            style={{ backgroundColor: "#198754", color: "#fff", fontSize: ".75rem" }}
            title={sentTo ? `Alıcı: ${sentTo}` : undefined}
        >
            Gönderildi
        </span>
    );
};

const showToast = (type, msg) => {
    const map = { danger: "error", error: "error" };
    const fn = toast[map[type] || type] || toast.info;
    fn(msg, { autoClose: 3000 });
};

const DUE_FILTERS = [
    { key: "ALL", label: "Tümü" },
    { key: "OVERDUE", label: "Vadesi Geçmiş" },
    { key: "UPCOMING", label: "Bekleyen" },
    { key: "PAID", label: "Ödenmiş" },
];

const getTodayInput = () => new Date().toISOString().slice(0, 10);

const InvoiceTrackingTable = () => {
    const navigate = useNavigate();

    const [accounts, setAccounts] = useState([]);
    const [loadingAccounts, setLoadingAccounts] = useState(true);
    const [selectedAccountId, setSelectedAccountId] = useState("");
    const [isAccountRole, setIsAccountRole] = useState(false);
    // Cari kendi borcunu görür: ona "vade" değil "borç" takibi.
    const pageTitle = isAccountRole ? "Borç Takibi" : "Vade Takibi";
    document.title = `${pageTitle} | Tahsilat360`;

    const [movements, setMovements] = useState([]);
    const [loadingMovements, setLoadingMovements] = useState(false);
    const [movementsError, setMovementsError] = useState(null);

    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [dueDateFilter, setDueDateFilter] = useState("ALL");
    const [searchTerm, setSearchTerm] = useState("");
    const [sortBy, setSortBy] = useState("dueDate");
    const [sortDir, setSortDir] = useState("asc");

    const [downloadingReconciliation, setDownloadingReconciliation] = useState(false);
    const [emailingReconciliation, setEmailingReconciliation] = useState(false);
    const [reconciliationEmailModalOpen, setReconciliationEmailModalOpen] = useState(false);
    const [reconciliationEmail, setReconciliationEmail] = useState("");
    const [reconciliationEmailError, setReconciliationEmailError] = useState("");
    const [reconciliationAccountId, setReconciliationAccountId] = useState("");
    // Mahsuplaşma dönem (manuel tarih aralığı) — boş ise backend varsayılanı (tüm geçmiş → bugün)
    // NOT: header'daki tarih seçicileri arkadaki tabloyu filtreler (bilinçli).
    const [reconStartDate, setReconStartDate] = useState("");
    const [reconEndDate, setReconEndDate] = useState("");
    // E-posta modalının KENDİ tarih aralığı — tabloyu ETKİLEMEZ; yalnızca gönderilen
    // e-posta/PDF dönemini belirler. Modal açılırken header aralığından ön-doldurulur.
    const [modalStartDate, setModalStartDate] = useState("");
    const [modalEndDate, setModalEndDate] = useState("");

    const [accountPickerOpen, setAccountPickerOpen] = useState(false);
    const [pendingAction, setPendingAction] = useState(null); // "DOWNLOAD" | "EMAIL"
    const [pickerAccountId, setPickerAccountId] = useState("");
    const [pickerError, setPickerError] = useState("");

    const [receiptModalOpen, setReceiptModalOpen] = useState(false);
    const [selectedDebt, setSelectedDebt] = useState(null);
    // Kısmi tahsilat: çift gönderim borcu iki kez kapatmış gibi gösterirdi.
    const receiptIdempotency = useIdempotencyKey();
    const [savingReceipt, setSavingReceipt] = useState(false);
    const [receiptType, setReceiptType] = useState("CASH");
    const [receiptForm, setReceiptForm] = useState({
        movementDate: getTodayInput(),
        amount: "",
        currency: "TRY",
        generalDescription: "",
        dueDate: "",
        checkNumber: "",
        promissoryNoteNumber: "",
        bankName: "",
    });

    const MOVEMENT_LIMIT = 10;

    // Tutar hareketin kendi para biriminde gösterilir: ₺2.000,00 / $2.000,00 / €2.000,00
    // Para birimi bilinmiyorsa TRY varsayılır.
    const formatCurrency = (amount, currency) => {
        if (amount == null) return "-";
        const num = Number(amount);
        const code = (currency || "TRY").toUpperCase();
        try {
            return num.toLocaleString("tr-TR", { style: "currency", currency: code });
        } catch {
            // Tanınmayan para birimi kodunda Intl patlar; tutarı kodla birlikte göster.
            return `${num.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${code}`;
        }
    };

    useEffect(() => {
        const loadAccounts = async () => {
            setLoadingAccounts(true);
            try {
                // Önce rol kontrolü — account rolleri cari select görmez
                const meRes = await axios.get(API.auth_me, {
                    headers: {},
                });
                const roles = meRes.data?.data?.roles || [];
                const isAccount = roles.some(r => r.startsWith("ROLE_ACCOUNT_"));
                setIsAccountRole(isAccount);

                if (!isAccount) {
                    const res = await axios.get(API.get_accounts_id_name, {
                        headers: {},
                    });
                    setAccounts(res.data?.data || []);
                }
            } catch (err) {
                showToast("error", err?.response?.data?.message || "Cari listesi yüklenemedi.");
            } finally {
                setLoadingAccounts(false);
            }
        };

        loadAccounts();
    }, []);

    useEffect(() => {
        fetchMovements(selectedAccountId, 0, dueDateFilter, sortBy, sortDir);
    }, [selectedAccountId, dueDateFilter, sortBy, sortDir, reconStartDate, reconEndDate]);

    const handleSort = (field) => {
        if (sortBy === field) {
            setSortDir(prev => prev === "asc" ? "desc" : "asc");
        } else {
            setSortBy(field);
            setSortDir("asc");
        }
        setCurrentPage(0);
    };

    const fetchMovements = async (accountId, page = 0, filter = dueDateFilter, by = sortBy, dir = sortDir) => {
        setLoadingMovements(true);
        setMovementsError(null);
        try {
            // Belirli cari seçiliyse tek-cari endpointi, yoksa tüm cariler için due-tracking
            const url = accountId
                ? API.account_movements(accountId, page, MOVEMENT_LIMIT, {
                    movementType: "DEBT",
                    dueDateFilter: filter,
                    startDate: reconStartDate || undefined,
                    endDate: reconEndDate || undefined,
                })
                : API.due_tracking_movements(page, MOVEMENT_LIMIT, {
                    movementType: "DEBT",
                    dueDateFilter: filter,
                    sortBy: by,
                    sortDir: dir,
                    startDate: reconStartDate || undefined,
                    endDate: reconEndDate || undefined,
                });

            const res = await axios.get(url);

            const pageInfo = res.data.data;
            const data = pageInfo.content || [];
            setMovements(data);
            setTotalPages(pageInfo.totalPages || 0);
            setCurrentPage(pageInfo.number || 0);
        } catch (err) {
            const status = err?.response?.status;
            const msg = err?.response?.data?.message;
            if (status === 400) {
                setMovementsError(msg || "Geçersiz filtre kombinasyonu.");
            } else {
                setMovementsError(msg || "Vade hareketleri yüklenirken bir hata oluştu.");
            }
            setMovements([]);
            setTotalPages(0);
            setCurrentPage(0);
        } finally {
            setLoadingMovements(false);
        }
    };

    const getApiErrorMessage = async (err, fallbackMessage) => {
        const status = err?.response?.status;
        if (status === 429) return "Çok fazla istek gönderdiniz. Lütfen 1 dakika sonra tekrar deneyin.";

        const data = err?.response?.data;
        if (data && typeof data === "object" && typeof data.message === "string") {
            return data.message;
        }

        if (data instanceof Blob) {
            try {
                const text = await data.text();
                const parsed = JSON.parse(text);
                if (parsed?.message) return parsed.message;
            } catch {
                return fallbackMessage;
            }
        }

        return fallbackMessage;
    };

    const getPdfFilenameFromHeaders = (headers) => {
        const contentDisposition = headers?.["content-disposition"] || headers?.["Content-Disposition"];
        if (!contentDisposition) return `mahsuplasma-${selectedAccountId || "cari"}.pdf`;
        const utfMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
        if (utfMatch?.[1]) return decodeURIComponent(utfMatch[1]);
        const basicMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
        return basicMatch?.[1] || `mahsuplasma-${selectedAccountId || "cari"}.pdf`;
    };

    // Rapor, tabloyu besleyen dönem/vade filtresiyle aynı kalemlerden üretilir; hareket
    // yokken istek boş PDF dönüyordu. Cari seçili değilse karar cari seçicide verilir.
    const reconciliationBlocked = Boolean(selectedAccountId) && !loadingMovements && movements.length === 0;

    const handleDownloadReconciliationPdf = async (accountIdOverride) => {
        const accountId = accountIdOverride || selectedAccountId;
        if (!accountId) {
            showToast("error", "Cari seçiniz.");
            return;
        }

        setDownloadingReconciliation(true);
        try {
            const payload = { accountId };
            if (reconStartDate) payload.startDate = reconStartDate;
            if (reconEndDate) payload.endDate = reconEndDate;
            const res = await axios.post(API.account_reconciliation_pdf_download, payload, {
                headers: {},
                responseType: "blob",
            });

            const blob = new Blob([res.data], { type: "application/pdf" });
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = downloadUrl;
            link.download = getPdfFilenameFromHeaders(res.headers);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(downloadUrl);

            showToast("success", "Mahsuplaşma PDF dosyası indirildi.");
        } catch (err) {
            showToast("error", await getApiErrorMessage(err, "Mahsuplaşma PDF indirilemedi."));
        } finally {
            setDownloadingReconciliation(false);
        }
    };

    const openReconciliationEmailModal = (accountIdOverride) => {
        const accountId = accountIdOverride || selectedAccountId;
        if (!accountId) {
            showToast("error", "Cari seçiniz.");
            return;
        }
        setReconciliationAccountId(accountId);
        setReconciliationEmail("");
        setReconciliationEmailError("");
        // Modal kendi tarih state'iyle açılır (header aralığından ön-dolu); modaldaki
        // değişiklikler tabloyu etkilemez.
        setModalStartDate(reconStartDate);
        setModalEndDate(reconEndDate);
        setReconciliationEmailModalOpen(true);
    };

    const closeReconciliationEmailModal = () => {
        if (emailingReconciliation) return;
        setReconciliationEmailModalOpen(false);
    };

    const handleEmailReconciliationPdf = async () => {
        // Buton zaten disabled; bu guard hızlı çift tıklamada ikinci isteği kesin olarak engeller.
        if (emailingReconciliation) return;
        if (!reconciliationAccountId) {
            showToast("error", "Cari seçiniz.");
            return;
        }

        const trimmedEmail = reconciliationEmail.trim();
        // Basit e-posta validasyonu — backend ayrıca @Email ile doğruluyor.
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!trimmedEmail || !emailRegex.test(trimmedEmail)) {
            setReconciliationEmailError("Geçerli bir e-posta adresi giriniz.");
            return;
        }
        setReconciliationEmailError("");

        setEmailingReconciliation(true);
        try {
            const payload = { accountId: reconciliationAccountId, recipientEmail: trimmedEmail };
            if (modalStartDate) payload.startDate = modalStartDate;
            if (modalEndDate) payload.endDate = modalEndDate;
            await axios.post(API.account_reconciliation_pdf_email, payload, {
                headers: {},
            });

            showToast("success", `Mahsuplaşma raporu ${trimmedEmail} adresine gönderildi.`);
            setReconciliationEmailModalOpen(false);
            // "Gönderildi / Gönderim Tarihi" sütunları sunucudaki damgayı okur; listeyi tazele ki
            // yeni gönderim anında sütunlara yansısın.
            fetchMovements(selectedAccountId, currentPage, dueDateFilter, sortBy, sortDir);
            // Başlıktaki "Son mahsuplaşma gönderimi" bilgisi cari listesinden okunuyor; onu da güncelle.
            setAccounts((prev) => prev.map((a) => a.id === reconciliationAccountId
                ? { ...a, lastReconciliationSentAt: new Date().toISOString(), lastReconciliationSentTo: trimmedEmail }
                : a));
        } catch (err) {
            showToast("error", await getApiErrorMessage(err, "Mahsuplaşma PDF e-posta gönderimi başarısız."));
        } finally {
            setEmailingReconciliation(false);
        }
    };

    const executeReconciliationAction = (action, accountId) => {
        if (action === "DOWNLOAD") {
            handleDownloadReconciliationPdf(accountId);
        } else if (action === "EMAIL") {
            openReconciliationEmailModal(accountId);
        }
    };

    const requireAccountThen = (action) => {
        if (selectedAccountId) {
            if (reconciliationBlocked) {
                showToast("error", "Seçili filtrelerde hareket bulunamadı, mahsuplaşma raporu oluşturulamaz.");
                return;
            }
            executeReconciliationAction(action, selectedAccountId);
            return;
        }
        if (isAccountRole || accounts.length === 0) {
            showToast("error", "Seçilebilecek cari bulunamadı.");
            return;
        }
        setPickerAccountId("");
        setPickerError("");
        setPendingAction(action);
        setAccountPickerOpen(true);
    };

    const closeAccountPicker = () => {
        if (downloadingReconciliation || emailingReconciliation) return;
        setAccountPickerOpen(false);
        setPendingAction(null);
        setPickerError("");
    };

    const confirmAccountPicker = () => {
        if (!pickerAccountId) {
            setPickerError("Lütfen bir cari seçiniz.");
            return;
        }
        const action = pendingAction;
        const id = pickerAccountId;
        setAccountPickerOpen(false);
        setPendingAction(null);
        setPickerError("");
        executeReconciliationAction(action, id);
    };

    const handlePageChange = (page) => {
        if (page < 0 || page >= totalPages || page === currentPage) return;
        fetchMovements(selectedAccountId, page, dueDateFilter, sortBy, sortDir);
    };

    const getPages = () => {
        const arr = [];
        const max = 5;
        let start = Math.max(0, currentPage - Math.floor(max / 2));
        let end = Math.min(totalPages - 1, start + max - 1);
        if (end - start + 1 < max) start = Math.max(0, end - max + 1);
        for (let i = start; i <= end; i++) arr.push(i);
        return arr;
    };

    const filteredRows = useMemo(() => {
        const query = searchTerm.trim().toLowerCase();
        if (!query) return movements;
        return movements.filter((item) => {
            const docNo = (item.documentNumber || "").toLowerCase();
            const desc = (item.generalDescription || "").toLowerCase();
            const accName = (item.accountName || "").toLowerCase();
            const accCode = (item.accountCode || "").toLowerCase();
            return docNo.includes(query) || desc.includes(query) || accName.includes(query) || accCode.includes(query);
        });
    }, [movements, searchTerm]);

    const parseTurkishAmount = (s) => {
        if (!s) return NaN;
        // "4.444.444,50" → "4444444.50" → float
        return parseFloat(s.replace(/\./g, "").replace(",", "."));
    };

    const formatTurkishAmount = (num) => {
        if (num == null || isNaN(num)) return "";
        return num.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const formatShortDate = (date) => {
        if (!date) return "-";
        return new Date(date).toLocaleDateString("tr-TR");
    };

    // Gönderim anı saatiyle birlikte anlamlı; sadece tarih yeterli bilgi vermiyor.
    const formatSentDateTime = (value) => {
        if (!value) return "—";
        return new Date(value).toLocaleString("tr-TR", {
            day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
        });
    };

    // Seçili carinin mahsuplaşma durumu. Cari listesinden okunur, hareketlerden değil:
    // borç hareketi olmayan cari tabloda hiç satır üretmez, o durumda tek görünür kaynak burasıdır.
    const selectedAccount = accounts.find((a) => a.id === selectedAccountId);

    const getDueDisplay = (item) => {
        if (item.invoiceStatus === "CLOSED") return { text: "Ödendi", color: "success" };
        if (item.dueDateLabel && item.overdue) return { text: item.dueDateLabel, color: "danger" };
        if (item.dueDateLabel) return { text: item.dueDateLabel, color: "warning" };
        if (!item.dueDate) return { text: "Vade yok", color: "secondary" };
        return { text: formatShortDate(item.dueDate), color: "secondary" };
    };

    const openReceiptModal = (debt) => {
        setSelectedDebt(debt);
        setReceiptType("CASH");
        setReceiptForm({
            movementDate: getTodayInput(),
            amount: debt ? formatTurkishAmount(Number(remainingOf(debt))) : "",
            currency: debt?.currency || "TRY",
            generalDescription: debt?.documentNumber ? `${debt.documentNumber} ödemesi` : "",
            dueDate: "",
            checkNumber: "",
            promissoryNoteNumber: "",
            bankName: "",
        });
        setReceiptModalOpen(true);
    };

    const closeReceiptModal = () => {
        if (savingReceipt) return;
        setReceiptModalOpen(false);
        setSelectedDebt(null);
    };

    const onReceiptChange = (e) => {
        const { name, value } = e.target;
        if (name === "amount") {
            // Sadece rakam, nokta (bin ayracı) ve virgül (ondalık) kabul et
            const filtered = value.replace(/[^\d.,]/g, "");
            setReceiptForm((prev) => ({ ...prev, amount: filtered }));
        } else {
            setReceiptForm((prev) => ({ ...prev, [name]: value }));
        }
    };

    // DateInput olay değil, doğrudan ISO tarih verir.
    const onReceiptDateChange = (name) => (isoDate) =>
        setReceiptForm((prev) => ({ ...prev, [name]: isoDate }));

    const onAmountBlur = () => {
        const num = parseTurkishAmount(receiptForm.amount);
        if (!isNaN(num) && num > 0) {
            setReceiptForm((prev) => ({ ...prev, amount: formatTurkishAmount(num) }));
        }
    };

    const validateReceipt = () => {
        if (!receiptForm.movementDate) return "Tahsilat tarihi zorunludur.";
        const parsedAmount = parseTurkishAmount(receiptForm.amount);
        if (!receiptForm.amount || isNaN(parsedAmount) || parsedAmount <= 0) {
            return "Geçerli bir tahsilat tutarı giriniz.";
        }
        if (receiptType === "CHECK" || receiptType === "PROMISSORY_NOTE") {
            if (!receiptForm.dueDate) return "Çek/Senet için vade tarihi zorunludur.";
            if (receiptType === "CHECK" && !receiptForm.checkNumber) return "Çek numarası zorunludur.";
            if (receiptType === "PROMISSORY_NOTE" && !receiptForm.promissoryNoteNumber) return "Senet numarası zorunludur.";
            if (receiptType === "CHECK" && !receiptForm.bankName) return "Çek tahsilatında banka seçiniz.";
        }
        return null;
    };

    const handleSaveReceipt = async () => {
        if (!selectedDebt?.id || !selectedDebt?.accountId) return;

        const validationError = validateReceipt();
        if (validationError) {
            showToast("error", validationError);
            return;
        }

        setSavingReceipt(true);
        try {
            const isCheck = receiptType === "CHECK";
            const isPromissoryNote = receiptType === "PROMISSORY_NOTE";

            const payload = {
                accountId: selectedDebt.accountId,
                amount: parseTurkishAmount(receiptForm.amount),
                currency: receiptForm.currency,
                movementDate: receiptForm.movementDate,
                generalDescription: receiptForm.generalDescription || null,
                receiptType,
                dueDate: isCheck || isPromissoryNote ? receiptForm.dueDate : null,
                checkNumber: isCheck ? receiptForm.checkNumber : null,
                promissoryNoteNumber: isPromissoryNote ? receiptForm.promissoryNoteNumber : null,
                bankName: isCheck ? receiptForm.bankName : null,
                linkedDebtMovementId: selectedDebt.id,
                documentNumber: null,
                invoiceStatus: null,
                vatAmount: null,
                itemsJsonSnapshot: null,
            };

            await axios.post(API.add_receipt, payload, {
                headers: { "Idempotency-Key": receiptIdempotency.keyRef.current },
            });
            receiptIdempotency.reset();

            // Fatura yalnız tutarın tamamı tahsil edildiğinde kapanır; kısmi tahsilatta
            // borç açık kalır, bu yüzden mesaj durumun değiştiğini iddia etmez.
            showToast("success", "Tahsilat kaydedildi.");
            closeReceiptModal();
            await fetchMovements(selectedAccountId, currentPage, dueDateFilter, sortBy, sortDir);
        } catch (err) {
            // Buradaki yanıt JSON; PDF uçlarındaki Blob'lu getApiErrorMessage'a gerek yok.
            // Ayrıca anahtar kararı da gerekiyor: sunucu reddettiyse tazelenir,
            // ağ hatası / 409 / 503'te korunur (bkz. utils/apiErrors.js).
            const detail = describeAxiosError(err, "Tahsilat kaydedilemedi.");
            if (detail.shouldResetKey) receiptIdempotency.reset();
            showToast("error", detail.message);
        } finally {
            setSavingReceipt(false);
        }
    };

    const handleCreatePaymentLink = (item) => {
        if (!item.companyId || !item.accountId) {
            showToast("error", "Link oluşturmak için cariye bağlı firma zorunludur.");
            return;
        }
        // Borcun para birimi de taşınır: döviz borçta tutar kendi biriminde kalır,
        // TL karşılığı ödeme anında firmanın güncel kuruyla hesaplanır.
        const currency = item.currency || "TRY";
        navigate(
            `/payment-links/create/${item.companyId}?accountId=${item.accountId}&amount=${remainingOf(item)}&currency=${currency}&debtMovementId=${item.id}`
        );
    };

    return (
        <div className="page-content vade-table">
            <style>{tableStyles}</style>
            <div className="container-fluid">
                <Row>
                    <Col>
                        <TableCard>
                            <CardHeader className="bg-white border-0 rounded-top-4" data-tour="invoice-tracking-header">
                                {/* DESKTOP header */}
                                <div className="d-none d-md-flex justify-content-between align-items-start">
                                    <div>
                                        <h5 className="mb-1 fw-bold text-dark">
                                            <i className="bx bx-time-five me-2" style={{ color: NAVY }}></i>
                                            {pageTitle}
                                        </h5>
                                        <p className="mb-0 text-muted" style={{ fontSize: ".85rem" }}>
                                            {isAccountRole ? "Vadeli borç hareketleriniz" : "Seçili carinin vadeli borç hareketleri"}
                                        </p>
                                        {/* Tablo boş olsa da gönderim durumu görünsün diye başlıkta da gösterilir. */}
                                        {selectedAccount && (
                                            <p className="mb-0 mt-1" style={{ fontSize: ".8rem" }}>
                                                {selectedAccount.lastReconciliationSentAt ? (
                                                    <span className="text-success">
                                                        <i className="bx bx-check-circle me-1"></i>
                                                        Son mahsuplaşma gönderimi: {formatSentDateTime(selectedAccount.lastReconciliationSentAt)}
                                                        {selectedAccount.lastReconciliationSentTo && ` · ${selectedAccount.lastReconciliationSentTo}`}
                                                    </span>
                                                ) : (
                                                    <span className="text-muted">
                                                        <i className="bx bx-info-circle me-1"></i>
                                                        Bu cariye henüz mahsuplaşma gönderilmedi.
                                                    </span>
                                                )}
                                            </p>
                                        )}
                                    </div>
                                    <div className="d-flex align-items-center header-actions">
                                        <Button
                                            color="light"
                                            className="btn-rounded border d-flex align-items-center gap-1"
                                            onClick={() => requireAccountThen("DOWNLOAD")}
                                            disabled={downloadingReconciliation || emailingReconciliation || reconciliationBlocked}
                                            title={reconciliationBlocked ? "Seçili filtrelerde hareket yok" : undefined}
                                        >
                                            {downloadingReconciliation
                                                ? <><Spinner size="sm" className="me-1" />İndiriliyor...</>
                                                : <><i className="bx bx-download"></i>Mahsuplaşma İndir</>}
                                        </Button>
                                        <Button
                                            style={{ backgroundColor: NAVY, borderColor: NAVY }}
                                            className="btn-rounded d-flex align-items-center gap-1"
                                            onClick={() => requireAccountThen("EMAIL")}
                                            disabled={emailingReconciliation || downloadingReconciliation || reconciliationBlocked}
                                            title={reconciliationBlocked ? "Seçili filtrelerde hareket yok" : undefined}
                                        >
                                            {emailingReconciliation
                                                ? <><Spinner size="sm" className="me-1" />Gönderiliyor...</>
                                                : <><i className="bx bx-envelope"></i>Mahsuplaşma Gönder</>}
                                        </Button>
                                    </div>
                                </div>

                                {/* Controls row — filters right-aligned */}
                                <div className="d-none d-md-flex align-items-center gap-2 flex-wrap mt-3 justify-content-end">
                                    <div className="d-flex align-items-center gap-1" title="Mahsuplaşma dönemi (boş = tüm geçmiş → bugün)">
                                        <i className="bx bx-calendar text-muted"></i>
                                        <DateInput
                                            className="pill-input"
                                            style={{ width: 150 }}
                                            value={reconStartDate}
                                            maxDate={reconEndDate}
                                            onChange={setReconStartDate}
                                            aria-label="Mahsuplaşma başlangıç tarihi"
                                        />
                                        <span className="text-muted">-</span>
                                        <DateInput
                                            className="pill-input"
                                            style={{ width: 150 }}
                                            value={reconEndDate}
                                            minDate={reconStartDate}
                                            onChange={setReconEndDate}
                                            aria-label="Mahsuplaşma bitiş tarihi"
                                        />
                                    </div>
                                    {!isAccountRole && (
                                        <Input
                                            type="select"
                                            className="form-select pill-select"
                                            style={{ width: 220 }}
                                            disabled={loadingAccounts}
                                            value={selectedAccountId}
                                            onChange={(e) => { setSelectedAccountId(e.target.value); setCurrentPage(0); }}
                                        >
                                            <option value="">Tüm Cariler</option>
                                            {accounts.map((a) => (
                                                <option key={a.id} value={a.id}>
                                                    {a.name || a.accountName || a.label || a.id}
                                                </option>
                                            ))}
                                        </Input>
                                    )}
                                    <Input
                                        type="select"
                                        className="form-select pill-select"
                                        style={{ width: 200 }}
                                        disabled={loadingMovements}
                                        value={dueDateFilter}
                                        onChange={(e) => { setDueDateFilter(e.target.value); setCurrentPage(0); }}
                                    >
                                        <option value="ALL">Tüm Durumlar</option>
                                        <option value="OVERDUE">Vadesi Geçmiş</option>
                                        <option value="UPCOMING">Bekleyen</option>
                                        <option value="PAID">Ödenmiş</option>
                                    </Input>
                                    <Input
                                        type="search"
                                        className="pill-input"
                                        style={{ width: 220 }}
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        placeholder={selectedAccountId ? "Belge no / açıklama ara..." : "Cari / belge no ara..."}
                                    />
                                </div>

                                {/* MOBILE header */}
                                <div className="d-md-none">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <h5 className="mb-0 fw-bold text-dark">
                                            <i className="bx bx-time-five me-2" style={{ color: NAVY }}></i>
                                            {pageTitle}
                                        </h5>
                                        <div className="d-flex gap-2">
                                            <Button color="light" size="sm" className="btn-rounded border"
                                                onClick={() => requireAccountThen("DOWNLOAD")}
                                                disabled={downloadingReconciliation || emailingReconciliation}>
                                                <i className="bx bx-download" />
                                            </Button>
                                            <Button color="primary" size="sm" className="btn-rounded"
                                                onClick={() => requireAccountThen("EMAIL")}
                                                disabled={emailingReconciliation || downloadingReconciliation}>
                                                <i className="bx bx-envelope" />
                                            </Button>
                                        </div>
                                    </div>
                                    <div className="d-flex flex-column gap-2">
                                        {!isAccountRole && (
                                            <Input
                                                type="select"
                                                className="form-select pill-select"
                                                disabled={loadingAccounts}
                                                value={selectedAccountId}
                                                onChange={(e) => { setSelectedAccountId(e.target.value); setCurrentPage(0); }}
                                            >
                                                <option value="">Tüm Cariler</option>
                                                {accounts.map((a) => (
                                                    <option key={a.id} value={a.id}>
                                                        {a.name || a.accountName || a.label || a.id}
                                                    </option>
                                                ))}
                                            </Input>
                                        )}
                                        <Input
                                            type="select"
                                            className="form-select pill-select"
                                            disabled={loadingMovements}
                                            value={dueDateFilter}
                                            onChange={(e) => { setDueDateFilter(e.target.value); setCurrentPage(0); }}
                                        >
                                            <option value="ALL">Tüm Durumlar</option>
                                            <option value="OVERDUE">Vadesi Geçmiş</option>
                                            <option value="UPCOMING">Bekleyen</option>
                                            <option value="PAID">Ödenmiş</option>
                                        </Input>
                                        <Input type="search" className="pill-input"
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            placeholder={selectedAccountId ? "Belge no / açıklama ara..." : "Cari / belge no ara..."} />
                                    </div>
                                </div>
                            </CardHeader>

                            <CardBody className="pt-2 rounded-bottom-4" data-tour="invoice-tracking-table">
                                {loadingMovements ? (
                                    <div className="text-center p-5">
                                        <Spinner size="sm" className="me-2" />
                                        <span className="text-muted small">Vade kayıtları yükleniyor...</span>
                                    </div>
                                ) : movementsError ? (
                                    <Alert color="danger" className="m-4 border-0 rounded-4">
                                        <i className="bx bx-error me-1"></i> {movementsError}
                                    </Alert>
                                ) : filteredRows.length > 0 ? (
                                    <>
                                        <div className="table-responsive rounded-4 border">
                                            <table className="table table-hover align-middle mb-0">
                                                <thead>
                                                    <tr>
                                                        {/* "Tüm Cariler" modunda cari kolonu göster */}
                                                        {!selectedAccountId && <th className="ps-4">Cari</th>}
                                                        <th className={selectedAccountId ? "ps-4" : ""}>Belge No</th>
                                                        <th>Tarih</th>
                                                        <th
                                                            style={!selectedAccountId ? { cursor: "pointer", userSelect: "none" } : {}}
                                                            onClick={!selectedAccountId ? () => handleSort("amount") : undefined}
                                                        >
                                                            Borç Tutarı
                                                            {!selectedAccountId && (
                                                                <i className={`bx ms-1 ${sortBy === "amount" ? (sortDir === "asc" ? "bx-sort-up" : "bx-sort-down") : "bx-sort"}`}
                                                                   style={{ opacity: sortBy === "amount" ? 1 : 0.35, fontSize: ".75rem" }} />
                                                            )}
                                                        </th>
                                                        <th>Kalan</th>
                                                        <th
                                                            style={!selectedAccountId ? { cursor: "pointer", userSelect: "none" } : {}}
                                                            onClick={!selectedAccountId ? () => handleSort("dueDate") : undefined}
                                                        >
                                                            Vade
                                                            {!selectedAccountId && (
                                                                <i className={`bx ms-1 ${sortBy === "dueDate" ? (sortDir === "asc" ? "bx-sort-up" : "bx-sort-down") : "bx-sort"}`}
                                                                   style={{ opacity: sortBy === "dueDate" ? 1 : 0.35, fontSize: ".75rem" }} />
                                                            )}
                                                        </th>
                                                        <th>Durum</th>
                                                        {/* Mahsuplaşma cari bazlı: tek cari modunda her satırda aynı değer
                                                            tekrar edeceği için sütunlar sadece "Tüm Cariler" modunda çıkar,
                                                            tek cari seçiliyken bilgi kart başlığında gösterilir. */}
                                                        {!selectedAccountId && <th>Gönderildi</th>}
                                                        {!selectedAccountId && <th>Gönderim Tarihi</th>}
                                                        <th className="text-end pe-4">İşlemler</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {filteredRows.map((item) => {
                                                        const due = getDueDisplay(item);
                                                        const isPaid = item.invoiceStatus === "CLOSED";
                                                        const dueDateValue = item.dueDate || item.movementDate;

                                                        return (
                                                            <tr key={item.id} className="border-bottom">
                                                                {/* Cari kolonu — sadece "Tüm Cariler" modunda */}
                                                                {!selectedAccountId && (
                                                                    <td className="ps-4 py-3">
                                                                        <div className="d-flex flex-column lh-sm">
                                                                            <span className="fw-medium">{item.accountName || "-"}</span>
                                                                            {item.accountCode && <small className="text-muted">{item.accountCode}</small>}
                                                                        </div>
                                                                    </td>
                                                                )}
                                                                <td className={`py-3${selectedAccountId ? " ps-4" : ""}`}>
                                                                    {item.documentNumber
                                                                        ? <span className="doc-code">{item.documentNumber}</span>
                                                                        : <span className="text-muted">—</span>}
                                                                </td>
                                                                <td className="py-3 text-muted">
                                                                    {formatShortDate(item.movementDate)}
                                                                </td>
                                                                <td className="py-3">
                                                                    <span className="fw-semibold" style={{ color: "#dc3545" }}>{formatCurrency(item.amount, item.currency)}</span>
                                                                </td>
                                                                <td className="py-3">
                                                                    <span className="fw-semibold" style={{ color: isPaid ? "#198754" : "#dc3545" }}>
                                                                        {formatCurrency(remainingOf(item), item.currency)}
                                                                    </span>
                                                                </td>
                                                                <td className="py-3">
                                                                    <div className="d-flex flex-column lh-sm">
                                                                        <span className="fw-medium">{formatShortDate(dueDateValue)}</span>
                                                                        <small className={`text-${due.color}`}>{due.text}</small>
                                                                    </div>
                                                                </td>
                                                                <td className="py-3"><StatusPill item={item} /></td>
                                                                {!selectedAccountId && (
                                                                    <td className="py-3">
                                                                        <ReconciliationSentPill
                                                                            sentAt={item.reconciliationSentAt}
                                                                            sentTo={item.reconciliationSentTo}
                                                                        />
                                                                    </td>
                                                                )}
                                                                {!selectedAccountId && (
                                                                    <td className="py-3 text-muted" style={{ whiteSpace: "nowrap" }}>
                                                                        {formatSentDateTime(item.reconciliationSentAt)}
                                                                    </td>
                                                                )}
                                                                <td className="py-3 text-end pe-4">
                                                                    <div className="d-inline-flex align-items-center gap-2">
                                                                        {!isAccountRole && (
                                                                            <Button size="sm" className="btn-navy btn-rounded"
                                                                                disabled={isPaid}
                                                                                onClick={() => handleCreatePaymentLink(item)}>
                                                                                <i className="bx bx-link-external me-1"></i>
                                                                                Link Oluştur
                                                                            </Button>
                                                                        )}
                                                                        <Button size="sm" className="btn-orange btn-rounded"
                                                                            disabled={isPaid}
                                                                            onClick={() => isAccountRole
                                                                                ? navigate(`/payment?type=normal&amount=${remainingOf(item)}`)
                                                                                : openReceiptModal(item)}>
                                                                            <i className="bx bx-money me-1"></i>
                                                                            Öde
                                                                        </Button>
                                                                        {/* Detay: borcun kısmi ödeme geçmişi */}
                                                                        <button
                                                                            className="btn btn-sm action-btn btn-orange"
                                                                            title="Detay"
                                                                            onClick={() => navigate(`/invoice-tracking/${item.id}`)}
                                                                        >
                                                                            <i className="bx bx-show" />
                                                                        </button>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>

                                        {totalPages > 1 && (
                                            <div className="d-flex justify-content-between align-items-center mt-3">
                                                <div className="text-muted" style={{ fontSize: ".9rem" }}>
                                                    Sayfa <strong>{currentPage + 1}</strong> / {totalPages}
                                                </div>
                                                <nav>
                                                    <ul className="pagination pagination-sm mb-0 corporate-pagination">
                                                        <li className={`page-item ${currentPage === 0 ? "disabled" : ""}`}>
                                                            <button className="page-link" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 0}>
                                                                <i className="bx bx-chevron-left" />
                                                            </button>
                                                        </li>
                                                        {getPages().map(p => (
                                                            <li key={p} className={`page-item ${currentPage === p ? "active" : ""}`}>
                                                                <button className="page-link" onClick={() => handlePageChange(p)}>{p + 1}</button>
                                                            </li>
                                                        ))}
                                                        <li className={`page-item ${currentPage >= totalPages - 1 ? "disabled" : ""}`}>
                                                            <button className="page-link" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage >= totalPages - 1}>
                                                                <i className="bx bx-chevron-right" />
                                                            </button>
                                                        </li>
                                                    </ul>
                                                </nav>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <div className="table-responsive rounded-4 border">
                                        <table className="table table-hover align-middle mb-0">
                                            <thead>
                                                <tr>
                                                    {!selectedAccountId && <th className="ps-4">Cari</th>}
                                                    <th className={selectedAccountId ? "ps-4" : ""}>Belge No</th>
                                                    <th>Tarih</th>
                                                    <th>Borç Tutarı</th>
                                                    <th>Kalan</th>
                                                    <th>Vade</th>
                                                    <th>Durum</th>
                                                    {!selectedAccountId && <th>Gönderildi</th>}
                                                    {!selectedAccountId && <th>Gönderim Tarihi</th>}
                                                    <th className="text-end pe-4">İşlemler</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td colSpan={selectedAccountId ? 7 : 10} className="text-center py-5">
                                                        <div className="text-muted">
                                                            <i className="bx bx-file display-4 d-block mb-2" />
                                                            Vade kaydı bulunamadı
                                                            <div className="mt-2"><small>Filtreleri temizlemeyi deneyin</small></div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </CardBody>
                        </TableCard>
                    </Col>
                </Row>
            </div>

            <Modal isOpen={accountPickerOpen} toggle={closeAccountPicker} centered>
                <ModalHeader toggle={closeAccountPicker} className="border-0 pb-0">
                    <h5 className="mb-0 fw-semibold">
                        <i className="bx bx-user-circle me-2" style={{ color: NAVY }}></i>
                        Cari Seçimi
                    </h5>
                </ModalHeader>
                <ModalBody className="pt-3">
                    <p className="text-muted small mb-3">
                        {pendingAction === "DOWNLOAD"
                            ? "Mahsuplaşma raporunu indirmek için bir cari seçin."
                            : "Mahsuplaşma raporunu göndermek için bir cari seçin."}
                    </p>
                    <FormGroup>
                        <Label className="small text-muted mb-1">Cari *</Label>
                        <Input
                            type="select"
                            className="form-select"
                            value={pickerAccountId}
                            onChange={(e) => {
                                setPickerAccountId(e.target.value);
                                if (pickerError) setPickerError("");
                            }}
                            invalid={!!pickerError}
                            autoFocus
                        >
                            <option value="">Cari Seçiniz</option>
                            {accounts.map((a) => (
                                <option key={a.id} value={a.id}>
                                    {a.name || a.accountName || a.label || a.id}
                                </option>
                            ))}
                        </Input>
                        {pickerError && (
                            <div className="invalid-feedback d-block">{pickerError}</div>
                        )}
                    </FormGroup>
                </ModalBody>
                <ModalFooter className="border-0">
                    <Button color="secondary" onClick={closeAccountPicker} className="rounded-pill px-4">
                        İptal
                    </Button>
                    <Button
                        style={{ backgroundColor: NAVY, borderColor: NAVY }}
                        onClick={confirmAccountPicker}
                        className="rounded-pill px-4"
                    >
                        {pendingAction === "DOWNLOAD" ? (
                            <><i className="bx bx-download me-1"></i>İndir</>
                        ) : (
                            <><i className="bx bx-right-arrow-alt me-1"></i>Devam Et</>
                        )}
                    </Button>
                </ModalFooter>
            </Modal>

            <Modal isOpen={reconciliationEmailModalOpen} toggle={closeReconciliationEmailModal} centered>
                <ModalHeader toggle={closeReconciliationEmailModal} className="border-0 pb-0">
                    <h5 className="mb-0 fw-semibold">
                        <i className="bx bx-envelope me-2" style={{ color: NAVY }}></i>
                        Mahsuplaşma E-posta Gönder
                    </h5>
                </ModalHeader>
                <ModalBody className="pt-3">
                    <p className="text-muted small mb-3">
                        Mahsuplaşma raporunun gönderileceği e-posta adresini girin.
                    </p>
                    <FormGroup>
                        <Label className="small text-muted mb-1">E-posta Adresi *</Label>
                        <Input
                            type="email"
                            value={reconciliationEmail}
                            onChange={(e) => {
                                setReconciliationEmail(e.target.value);
                                if (reconciliationEmailError) setReconciliationEmailError("");
                            }}
                            placeholder="ornek@firma.com"
                            invalid={!!reconciliationEmailError}
                            disabled={emailingReconciliation}
                            autoFocus
                        />
                        {reconciliationEmailError && (
                            <div className="invalid-feedback d-block">{reconciliationEmailError}</div>
                        )}
                    </FormGroup>
                    <Row className="g-2">
                        <Col xs={6}>
                            <FormGroup className="mb-0">
                                <Label className="small text-muted mb-1">Başlangıç (ops.)</Label>
                                <DateInput
                                    value={modalStartDate}
                                    maxDate={modalEndDate}
                                    onChange={setModalStartDate}
                                    disabled={emailingReconciliation}
                                />
                            </FormGroup>
                        </Col>
                        <Col xs={6}>
                            <FormGroup className="mb-0">
                                <Label className="small text-muted mb-1">Bitiş (ops.)</Label>
                                <DateInput
                                    value={modalEndDate}
                                    minDate={modalStartDate}
                                    onChange={setModalEndDate}
                                    disabled={emailingReconciliation}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    <p className="text-muted mb-0 mt-2" style={{ fontSize: ".72rem" }}>
                        Tarih seçilmezse tüm geçmiş hareketler bugüne kadar dahil edilir.
                    </p>
                </ModalBody>
                <ModalFooter className="border-0">
                    <Button color="secondary" onClick={closeReconciliationEmailModal} disabled={emailingReconciliation} className="rounded-pill px-4">
                        İptal
                    </Button>
                    <Button
                        style={{ backgroundColor: NAVY, borderColor: NAVY }}
                        onClick={handleEmailReconciliationPdf}
                        disabled={emailingReconciliation}
                        className="rounded-pill px-4"
                    >
                        {emailingReconciliation ? (
                            <><Spinner size="sm" className="me-2" />Gönderiliyor...</>
                        ) : (
                            <><i className="bx bx-send me-1"></i>Gönder</>
                        )}
                    </Button>
                </ModalFooter>
            </Modal>

            <Modal isOpen={receiptModalOpen} toggle={closeReceiptModal} centered size="lg">
                <ModalHeader toggle={closeReceiptModal} className="border-0 pb-0">
                    <h5 className="mb-0 fw-semibold">Manuel Tahsilat Girişi</h5>
                </ModalHeader>
                <ModalBody className="pt-3">
                    {selectedDebt && (
                        <Alert color="light" className="border rounded-4">
                            <strong>{selectedDebt.documentNumber || "Belge"}</strong> - {formatCurrency(selectedDebt.amount, selectedDebt.currency)}
                        </Alert>
                    )}

                    <Row className="g-3">
                        <Col md={6}>
                            <FormGroup>
                                <Label className="small text-muted mb-1">Tahsilat Tipi *</Label>
                                <Input type="select" value={receiptType} onChange={(e) => setReceiptType(e.target.value)}>
                                    <option value="CASH">Nakit</option>
                                    <option value="BANK_TRANSFER">Havale/EFT</option>
                                    <option value="CHECK">Çek</option>
                                    <option value="PROMISSORY_NOTE">Senet</option>
                                </Input>
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            <FormGroup>
                                <Label className="small text-muted mb-1">Tahsilat Tarihi *</Label>
                                <DateInput value={receiptForm.movementDate} onChange={onReceiptDateChange("movementDate")} />
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row className="g-3">
                        <Col md={8}>
                            <FormGroup>
                                <Label className="small text-muted mb-1">Tutar *</Label>
                                <Input
                                    type="text"
                                    inputMode="decimal"
                                    name="amount"
                                    value={receiptForm.amount}
                                    onChange={onReceiptChange}
                                    onBlur={onAmountBlur}
                                    placeholder="0,00"
                                />
                            </FormGroup>
                        </Col>
                        <Col md={4}>
                            <FormGroup>
                                <Label className="small text-muted mb-1">Para Birimi *</Label>
                                <Input type="select" name="currency" value={receiptForm.currency} onChange={onReceiptChange}>
                                    {CURRENCIES.map((cur) => (
                                        <option key={cur.code} value={cur.code}>{cur.label}</option>
                                    ))}
                                </Input>
                            </FormGroup>
                        </Col>
                    </Row>

                    {(receiptType === "CHECK" || receiptType === "PROMISSORY_NOTE") && (
                        <Row className="g-3">
                            <Col md={6}>
                                <FormGroup>
                                    <Label className="small text-muted mb-1">Vade *</Label>
                                    <DateInput value={receiptForm.dueDate} onChange={onReceiptDateChange("dueDate")} />
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                {receiptType === "CHECK" ? (
                                    <FormGroup>
                                        <Label className="small text-muted mb-1">Çek No *</Label>
                                        <Input type="text" name="checkNumber" value={receiptForm.checkNumber} onChange={onReceiptChange} />
                                    </FormGroup>
                                ) : (
                                    <FormGroup>
                                        <Label className="small text-muted mb-1">Senet No *</Label>
                                        <Input type="text" name="promissoryNoteNumber" value={receiptForm.promissoryNoteNumber} onChange={onReceiptChange} />
                                    </FormGroup>
                                )}
                            </Col>
                            {receiptType === "CHECK" && (
                                <Col md={12}>
                                    <FormGroup>
                                        <Label className="small text-muted mb-1">Banka *</Label>
                                        <Input type="select" name="bankName" value={receiptForm.bankName} onChange={onReceiptChange}>
                                            <option value="">Banka Seçiniz</option>
                                            {TURKISH_BANKS.map((bank) => (
                                                <option key={bank} value={bank}>{bank}</option>
                                            ))}
                                        </Input>
                                    </FormGroup>
                                </Col>
                            )}
                        </Row>
                    )}

                    <FormGroup>
                        <Label className="small text-muted mb-1">Açıklama</Label>
                        <Input
                            type="textarea"
                            rows="2"
                            name="generalDescription"
                            value={receiptForm.generalDescription}
                            onChange={onReceiptChange}
                            placeholder="Opsiyonel"
                        />
                    </FormGroup>
                </ModalBody>
                <ModalFooter className="border-0">
                    <Button color="secondary" onClick={closeReceiptModal} disabled={savingReceipt} className="rounded-pill px-4">
                        İptal
                    </Button>
                    <Button color="primary" onClick={handleSaveReceipt} disabled={savingReceipt} className="rounded-pill px-4">
                        {savingReceipt ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Kaydediliyor...
                            </>
                        ) : (
                            <>
                                <i className="bx bx-check me-1"></i>
                                Tahsilat Kaydet
                            </>
                        )}
                    </Button>
                </ModalFooter>
            </Modal>

            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                pauseOnFocusLoss
                draggable
                pauseOnHover
            />
        </div>
    );
};

export default InvoiceTrackingTable;
